import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Block, Typography } from '@aloft/toolkit-sui';

import { FieldPath, useWatch } from 'react-hook-form';

import { PropertyGridForm } from '../../types.ts';
import { EditableDateField } from '../EditableDateField.tsx';
import { readOnlyFieldTypographyProps } from '../styles.ts';
import { PropertyGridFieldProps } from '../types.ts';

type ConditionalDateFieldProps = PropertyGridFieldProps & {
  statusFieldName: FieldPath<PropertyGridForm>;
  allowedStatuses: string[];
};

const ConditionalDateField = ({
  columnIndex,
  field,
  control,
  getValues,
  setValue,
  colSpan,
  required,
  statusFieldName,
  allowedStatuses,
}: ConditionalDateFieldProps) => {
  const status = useWatch({ control, name: statusFieldName }) as string | undefined;
  const propertyId = getValues(`columns.${columnIndex}.propertyId`);
  const isSubject = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

  if (status && !allowedStatuses.includes(status)) {
    return (
      <Block gridColumn={`span ${colSpan}`}>
        <Typography {...readOnlyFieldTypographyProps}>-</Typography>
      </Block>
    );
  }

  // `required` is only meaningful for the status(es) this date field is shown for (e.g. Sale
  // Date is only required once Status is Sold), and never for the subject, which has no
  // transaction details.
  const isRequired = Boolean(required) && !isSubject && !!status && allowedStatuses.includes(status);

  return (
    <EditableDateField
      columnIndex={columnIndex}
      field={field}
      control={control}
      getValues={getValues}
      setValue={setValue}
      colSpan={colSpan}
      required={isRequired}
    />
  );
};

export const SoldDateField = ({ columnIndex, ...props }: PropertyGridFieldProps) => (
  <ConditionalDateField
    {...props}
    columnIndex={columnIndex}
    statusFieldName={
      `columns.${columnIndex}.propertyData.editableData.transaction.status` as FieldPath<PropertyGridForm>
    }
    allowedStatuses={['SettledSale']}
  />
);

export const ContractDateField = ({ columnIndex, ...props }: PropertyGridFieldProps) => (
  <ConditionalDateField
    {...props}
    columnIndex={columnIndex}
    statusFieldName={
      `columns.${columnIndex}.propertyData.editableData.transaction.status` as FieldPath<PropertyGridForm>
    }
    allowedStatuses={['SettledSale', 'Contract']}
  />
);

export const LeaseDateField = ({ columnIndex, ...props }: PropertyGridFieldProps) => (
  <ConditionalDateField
    {...props}
    columnIndex={columnIndex}
    statusFieldName={
      `columns.${columnIndex}.propertyData.editableData.transaction.rentalListingStatus` as FieldPath<PropertyGridForm>
    }
    allowedStatuses={['Rented']}
  />
);
